/* eslint-disable camelcase */
import React, { CSSProperties, useContext, useMemo } from 'react';
import { type ChannelPreviewUIComponentProps } from 'stream-chat-react';
import { formatTimeStr, getOtherParticipantsFullNames } from 'utils';
import { Tooltip, Text, theme, Icon, ListingImage, Avatar } from 'components';
import styled from 'styled-components';
import { ContextualChatContext, RightSidePanelPageType } from 'contexts';
import { UserResponse } from 'stream-chat';
import {
  CHANNEL_LAST_MESSSAGE_TRUNCATE_SIZE,
  CHANNEL_NAME_LIST_TRUNCATE_SIZE,
  CHANNEL_MEMBERS_SUBTEXT_TRUNCATE_SIZE,
  AVATAR_CHANNEL_LIST_SIZE_WITH_PADDING,
} from './constants';

const API_KEY = process.env.FILESTACK_API_KEY;
const BASE_URL = 'https://cdn.filestackcontent.com';

const ChannelListListingItemComponent = (
  props: Partial<ChannelPreviewUIComponentProps> & {
    userId: string;
    expanded: boolean;
    isSearchItem?: boolean;
  },
) => {
  const { channel, userId, expanded } = props;

  const {
    channel: activeChannel,
    setChannel,
    setSelectedContactId,
    setRightSidePanelPage,
  } = useContext(ContextualChatContext);
  const onSelectChannel = () => {
    // Reset card details
    setSelectedContactId('');
    setRightSidePanelPage(RightSidePanelPageType.ChatDetails);
    // Navigate to desired channel on the main modal container
    setChannel(channel);
  };
  const { last_message_at: lastMessageAt, name: channelName } = channel.data;

  const { unreadCount, messages } = channel.state;

  const { listingThumbnail } = channel.data;

  const lastMessage = useMemo(() => messages[messages.length - 1], [messages]);
  const isMuted = channel.muteStatus().muted;

  const chatMembersArray = useMemo(
    () =>
      Object.values(channel.state.members).reduce<UserResponse[]>(
        (acc, member) => {
          if (member.user.id !== userId) acc.push(member.user);
          return acc;
        },
        [],
      ),
    [channel, userId],
  );

  const chatMembers = useMemo(
    () => getOtherParticipantsFullNames(channel, userId),
    [channel, userId],
  );

  const getAvatar = (user: UserResponse, properties: CSSProperties) => {
    const { name, avatarFilestackId } = user;
    const names = name?.split(' ');
    const srcUrl = `${BASE_URL}/${API_KEY}/resize=width:32,height:32/${avatarFilestackId}`;

    return (
      <StyledThumbnailAvatarContainer style={{ ...properties }}>
        <Avatar
          user={{
            firstName: names?.at(0) || '',
            lastName: names?.at(1) || '',
          }}
          width={27}
          src={avatarFilestackId ? srcUrl : undefined}
        />
      </StyledThumbnailAvatarContainer>
    );
  };

  const getAvatars = () => {
    if (!chatMembersArray.length) {
      return;
    }

    if (chatMembersArray.length >= 2) {
      return (
        <>
          {getAvatar(chatMembersArray.at(0), { bottom: 5, right: 0 })}
          {getAvatar(chatMembersArray.at(1), { bottom: -10, left: 10 })}
        </>
      );
    }

    return getAvatar(chatMembersArray.at(0), { bottom: 0, right: 0 });
  };

  const lastMessagePreview = useMemo(() => {
    if (!lastMessage) return '';

    if (lastMessage.type === 'deleted') {
      return 'Message deleted';
    }

    if (lastMessage.text) {
      return lastMessage.text;
    }

    if (lastMessage.attachments?.length) {
      const attachment = lastMessage.attachments[0];
      return `Attachment: ${attachment?.title ?? attachment?.fallback}`;
    }

    return null;
  }, [lastMessage]);

  return (
    <StyledChannelItemContainer
      expanded={expanded}
      $active={channel.id === activeChannel?.id}
      onClick={onSelectChannel}
    >
      {expanded && (
        <>
          {!!unreadCount && (
            <StyledBadgeContainer>
              {unreadCount < 10 ? unreadCount : '9+'}
            </StyledBadgeContainer>
          )}
          {!!lastMessageAt && (
            <StyledTimeText $newMessageHighlight={!!unreadCount}>
              {formatTimeStr(lastMessageAt)}
            </StyledTimeText>
          )}
        </>
      )}

      <StyledThumbnailContainer>
        <ListingImage imageSrc={listingThumbnail} size={45} />
        {getAvatars()}
      </StyledThumbnailContainer>

      {expanded && (
        <StyledChatDetails>
          <StyledChannelName ellipsis={CHANNEL_NAME_LIST_TRUNCATE_SIZE}>
            {channelName}
          </StyledChannelName>
          <Tooltip
            placement="bottomLeft"
            title={chatMembers}
            trigger={
              // Only trigger if the members length creates ellipsis
              chatMembers.length > 29 ? 'hover' : ''
            }
          >
            <StyledChannelMembers
              ellipsis={CHANNEL_MEMBERS_SUBTEXT_TRUNCATE_SIZE}
            >
              {chatMembers}
            </StyledChannelMembers>
          </Tooltip>
          {lastMessagePreview && (
            <FlexContainer>
              <StyledLatestMessagePreview
                color={theme.getColor('textSecondary')}
                ellipsis={CHANNEL_LAST_MESSSAGE_TRUNCATE_SIZE}
                // Do not move width to styled context, gets applied differently (internally)
                style={{ width: '100%' }}
              >
                {lastMessagePreview}
              </StyledLatestMessagePreview>
              {lastMessage?.attachments?.length ? (
                <Icon
                  type="antdPaperClipOutlined"
                  width={18}
                  color="textSecondary"
                  style={{ marginRight: isMuted ? '6px' : undefined }}
                />
              ) : null}
              {isMuted && (
                <Icon type="bellOutlined" color="textPrimary" width={15} />
              )}
            </FlexContainer>
          )}
        </StyledChatDetails>
      )}
    </StyledChannelItemContainer>
  );
};

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledThumbnailContainer = styled.div`
  padding-right: 5px;
  padding-bottom: 5px;
  position: relative;
`;

const StyledThumbnailAvatarContainer = styled.div`
  background-color: #fff;
  padding: 2px;
  border-radius: 30px;
  width: fit-content;
  position: absolute;
`;

const StyledChannelItemContainer = styled.button<{
  $active: boolean;
  expanded: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 14px;
  border: 1px solid #f0f0f0;
  gap: 10px;
  justify-content: ${({ expanded }) => (!expanded ? 'center' : undefined)};
  width: 100%;
  background-color: ${({ $active }) => ($active ? '#F0F7FF' : '#FFFFFF')};
  min-height: 90px;
  max-height: 90px;
  &:hover {
    cursor: pointer;
    background-color: #f0f7ff;
  }
`;

const StyledBadgeContainer = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2px;
  border-radius: 10px;
  padding-right: 3px;
  padding-left: 3px;
  height: 20px;
  width: 20px;
  color: ${theme.getColor('white')};
  background-color: ${theme.getColor('primary')};
`;

const StyledTimeText = styled(Text)<{ $newMessageHighlight: boolean }>`
  position: absolute;
  right: 14px;
  top: 14px;

  color: ${({ $newMessageHighlight }) =>
    $newMessageHighlight
      ? theme.getColor('primary')
      : theme.getColor('textSecondary')};
  margin: 0;
  font-size: 12px;
`;

const StyledChatDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  width: calc(100% - ${AVATAR_CHANNEL_LIST_SIZE_WITH_PADDING}px);
`;

const StyledChannelName = styled(Text)`
  font-weight: 700;
  margin: 0;
  font-size: 15px;
`;

const StyledChannelMembers = styled(Text)`
  font-weight: 600;
  margin: 0;
  font-size: 12px;
`;

const StyledLatestMessagePreview = styled(Text)`
  margin: 0;
`;

const ChannelListListingItem = React.memo(ChannelListListingItemComponent);

export default ChannelListListingItem;
